import { useState, useEffect } from 'react';
import { PhoneIcon, MailIcon, LinkIcon, HomeIcon } from 'lucide-react';
import { tenantApi } from '../../lib/tenant-api';

export default function ContactsSection() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [contactForm, setContactForm] = useState({
    company_name: '',
    company_contact_fullname: '',
    company_email: '',
    contact_phone: '',
    contact_email: '',
    contact_url: '',
    contact_address: '',
    sales_url: ''
  });

  useEffect(() => {
    loadContactInfo();
  }, []);

  const loadContactInfo = async () => {
    try {
      setLoading(true);
      const contactResponse = await tenantApi.getContactInfo();
      if (contactResponse) {
        const contactData = contactResponse as typeof contactResponse & { company_contact_fullname?: string };
        setContactForm({
          company_name: contactData.company_name || '',
          company_contact_fullname: contactData.company_contact_fullname || '',
          company_email: contactData.company_email || '',
          contact_phone: contactData.contact_phone || '',
          contact_email: contactData.contact_email || '',
          contact_url: contactData.contact_url || '',
          contact_address: contactData.address || contactData.contact_address || '',
          sales_url: contactData.sales_url || ''
        });
      }
    } catch (error) {
      console.error('Failed to load contact data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      await tenantApi.createOrUpdateContactInfo(contactForm);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 5000);
    } catch (error) {
      console.error('Failed to save contact info:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white">
      {showSuccess && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3  flex items-center">
          <svg className="h-5 w-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="font-medium">Contact information saved successfully!</span>
        </div>
      )}
      <div className="mb-4">
        <h2 className="text-lg font-medium text-gray-900 flex items-center">
          <PhoneIcon className="h-5 w-5 mr-2 text-indigo-600" />
          Company Contact Information
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          Manage your company information
        </p>
      </div>

      <form onSubmit={handleContactSubmit}>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 mb-8">
          <div>
            <label htmlFor="company_name" className="block text-sm font-medium text-gray-700">
              Company Name
            </label>
            <div className="mt-1 relative  shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <HomeIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                id="company_name"
                value={contactForm.company_name}
                onChange={(e) => setContactForm({ ...contactForm, company_name: e.target.value })}
                disabled={saving || loading}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300  focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Your Company Name"
              />
            </div>
          </div>

          <div>
            <label htmlFor="company_contact_fullname" className="block text-sm font-medium text-gray-700">
              Company Contact Fullname
            </label>
            <div className="mt-1 relative  shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <HomeIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                id="company_contact_fullname"
                value={contactForm.company_contact_fullname}
                onChange={(e) => setContactForm({ ...contactForm, company_contact_fullname: e.target.value })}
                disabled={saving || loading}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300  focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Jane Doe"
              />
            </div>
          </div>

          <div>
            <label htmlFor="company_email" className="block text-sm font-medium text-gray-700">
              Company Contact Email
            </label>
            <div className="mt-1 relative  shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MailIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="email"
                id="company_email"
                value={contactForm.company_email}
                onChange={(e) => setContactForm({ ...contactForm, company_email: e.target.value })}
                disabled={saving || loading}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300  focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="company@example.com"
              />
            </div>
          </div>
        </div>

        <div className="mb-4 pt-6 border-t border-gray-200">
          <h2 className="text-lg font-medium text-gray-900 flex items-center">
            <PhoneIcon className="h-5 w-5 mr-2 text-indigo-600" />
            Widget Contact Information
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            Manage your business contact details that will be available to the AI widget
          </p>
          <p className="mt-2 text-xs text-amber-600 bg-amber-50 border border-amber-200  px-3 py-2 flex items-start">
            <svg className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span>This information may be visible to customers through the AI widget.</span>
          </p>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="contact_phone" className="block text-sm font-medium text-gray-700">
              Phone Number
            </label>
            <div className="mt-1 relative  shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <PhoneIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="tel"
                id="contact_phone"
                value={contactForm.contact_phone}
                onChange={(e) => setContactForm({ ...contactForm, contact_phone: e.target.value })}
                disabled={saving || loading}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300  focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="+1 (555) 123-4567"
              />
            </div>
          </div>

          <div>
            <label htmlFor="contact_email" className="block text-sm font-medium text-gray-700">
              Email Address
            </label>
            <div className="mt-1 relative  shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MailIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="email"
                id="contact_email"
                value={contactForm.contact_email}
                onChange={(e) => setContactForm({ ...contactForm, contact_email: e.target.value })}
                disabled={saving || loading}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300  focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="contact@company.com"
              />
            </div>
          </div>

          <div>
            <label htmlFor="contact_url" className="block text-sm font-medium text-gray-700">
              Website URL
            </label>
            <div className="mt-1 relative  shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <LinkIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="url"
                id="contact_url"
                value={contactForm.contact_url}
                onChange={(e) => setContactForm({ ...contactForm, contact_url: e.target.value })}
                disabled={saving || loading}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300  focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="https://www.company.com"
              />
            </div>
          </div>

          <div>
            <label htmlFor="sales_url" className="block text-sm font-medium text-gray-700">
              Sales URL
            </label>
            <div className="mt-1 relative  shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <LinkIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="url"
                id="sales_url"
                value={contactForm.sales_url}
                onChange={(e) => setContactForm({ ...contactForm, sales_url: e.target.value })}
                disabled={saving || loading}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300  focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="https://sales.company.com"
              />
            </div>
          </div>

          <div>
            <label htmlFor="contact_address" className="block text-sm font-medium text-gray-700">
              Address
            </label>
            <div className="mt-1 relative  shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <HomeIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                id="contact_address"
                value={contactForm.contact_address || ''}
                onChange={(e) => setContactForm({ ...contactForm, contact_address: e.target.value })}
                disabled={saving || loading}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300  focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="123 Main St, City, Country"
              />
            </div>
          </div>
        </div>

        <div className="mt-6">
          <button
            type="submit"
            disabled={saving || loading}
            className="inline-flex justify-center items-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium  text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {(saving || loading) && (
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {(() => {
              if (loading) return 'Loading...';
              if (saving) return 'Saving...';
              return 'Save Contact Information';
            })()}
          </button>
        </div>
      </form>
    </div>
  );
}
